import React, { useState } from "react";
import { navigate } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import Devis24h from "../../assets/icons/devis-sous-24_bleu.svg";
import Maquette from "../../assets/icons/maquette-virtuelle_bleu.svg";
import Livraison7 from "../../assets/icons/livraison_bleu.svg";
import France from "../../assets/icons/impression-francaise_bleu.svg";
import Cross from "../../assets/icons/cross.svg";
import AvisVerifies from "../../assets/icons/logo_avis-verifies.svg";

const engagements = [
  { Icon: Devis24h, text: <>Devis<br />sous 24 h</> },
  { Icon: Maquette, text: <>Maquette<br />virtuelle</> },
  { Icon: Livraison7, text: <>Produit &amp; expédié<br />sous 7 à 10 jours ouvrés</> },
  { Icon: France, text: <>Impression<br />française</> },
];

const labelClass = "block mb-2 text-sm font-semibold text-gray-800";
const fieldClass =
  "block w-full mb-0 px-4 h-12 shadow-none text-base bg-white border-1 border-gray-300 rounded-lg focus:outline-none focus:border-blue";

const itemImage = (cartItem) =>
  (cartItem.variation && cartItem.variation.Image && getImage(cartItem.variation.Image.localFile)) ||
  (cartItem.product && cartItem.product.image && getImage(cartItem.product.image.localFile));

const colorName = (cartItem) =>
  cartItem.color && cartItem.color.Nom ? cartItem.color.Nom.replace(/-/g, " ") : "—";

const Detail = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase text-gray-500 mb-1">{label}</div>
    <div className="font-semibold text-gray-800 first-letter:uppercase">{children}</div>
  </div>
);

const CartItem = ({ cartItem, compact, onRemove }) => {
  const image = itemImage(cartItem);
  return (
    <div className={compact ? "flex gap-4 py-4 border-b-1 border-gray-200 last:border-b-0" : ""}>
      {image && (
        <div className={compact ? "w-20 flex-shrink-0" : "mb-6"}>
          <GatsbyImage
            image={image}
            alt={cartItem.product.title}
            className="rounded-lg bg-white"
            objectFit="contain"
            style={{ height: compact ? 80 : 260, width: "100%" }}
          />
        </div>
      )}
      <div className="flex-grow">
        <div className="flex justify-between items-start gap-3 mb-4">
          <p className={`font-bold text-blue ${compact ? "text-base" : "text-2xl"}`}>
            {cartItem.product.title}
          </p>
          <button
            type="button"
            onClick={onRemove}
            className="bg-transparent border-none p-1 mt-1 flex-shrink-0 opacity-60 hover:opacity-100"
            aria-label={`Retirer ${cartItem.product.title} de ma sélection`}
            title="Retirer de ma sélection"
          >
            <Cross width={12} height={12} />
          </button>
        </div>
        <div className={`grid grid-cols-2 ${compact ? "gap-3 text-sm" : "gap-x-4 gap-y-5"}`}>
          <Detail label="Quantité">{cartItem.quantity} exemplaires</Detail>
          <Detail label="Couleur">
            <span className="inline-flex items-center gap-2">
              {cartItem.color && cartItem.color.valeur && (
                <span
                  className="inline-block w-3 h-3 rounded-full border-1 border-gray-300"
                  style={{ backgroundColor: cartItem.color.valeur }}
                />
              )}
              {colorName(cartItem)}
            </span>
          </Detail>
          <Detail label="Marquage">{cartItem.marquage || "—"}</Detail>
          <Detail label="Impression">
            {cartItem.faces} face{+cartItem.faces > 1 ? "s" : ""}
          </Detail>
        </div>
      </div>
    </div>
  );
};

const FileDrop = ({ name, id, title, hint }) => {
  const [fileName, setFileName] = useState("");
  return (
    <label
      htmlFor={id}
      className="flex items-center gap-4 p-4 mb-0 rounded-lg border-2 border-dashed border-gray-300 bg-lightGray cursor-pointer hover:border-blue transition-colors"
    >
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-lighter text-blue flex-shrink-0">
        {/* SVG plutôt que le caractère « + », dont la position verticale dépend de la police */}
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-blue truncate">{fileName || title}</span>
        <span className="block text-xs text-gray-500">
          {fileName ? "Cliquez pour changer de fichier" : hint}
        </span>
      </span>
      <input
        name={name}
        id={id}
        type="file"
        accept=".pdf,.ai,.eps,.svg,.png,.jpg,.jpeg"
        className="sr-only"
        onChange={(e) => setFileName(e.target.files[0] ? e.target.files[0].name : "")}
      />
    </label>
  );
};

const CartModal = ({ open, cart, onClose, onRemove, onSubmitted, utmParams = {} }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { utm_campaign, utm_term, gad_source } = utmParams;
  const compact = cart.length > 1;

  const handleSubmit = (e) => {
    e.preventDefault();

    // Empêcher les soumissions multiples
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData(document.getElementById("devis-form"));
    fetch("/", {
      method: "POST",
      body: formData,
    })
      .then(() => {
        onSubmitted();
        navigate("/remerciements");
      })
      .catch((error) => {
        setIsSubmitting(false);
        alert(error);
      });
  };

  return (
    <div className={open ? "flex justify-center" : "hidden"}>
      <div className="cart-backdrop" onClick={onClose} />
      <div
        className="cart-modal w-full md:w-11/12 xl:w-10/12 rounded-2xl shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 z-10 bg-transparent border-none p-2 opacity-70 hover:opacity-100"
          aria-label="Fermer"
        >
          <Cross width={16} height={16} />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Sélection */}
          <div className="md:w-5/12 bg-lightGray p-6 md:p-10">
            <p className="text-sm font-bold uppercase tracking-widest text-orange mb-6">
              Votre sélection
            </p>
            {cart.map((cartItem, index) => (
              <CartItem
                key={index}
                cartItem={cartItem}
                compact={compact}
                onRemove={onRemove(index)}
              />
            ))}
            <button
              type="button"
              onClick={onClose}
              className="mt-8 bg-transparent border-none p-0 font-semibold text-blue hover:underline"
            >
              ← Modifier ma sélection
            </button>
          </div>

          {/* Formulaire */}
          <form
            id="devis-form"
            className="md:w-7/12 p-6 md:p-10"
            onSubmit={handleSubmit}
            name="demander-devis"
            data-netlify="true"
            netlify-honeypot="potdemiel"
          >
            <input type="hidden" name="form-name" value="demander-devis" />
            <input type="hidden" name="gad_source" value={gad_source || "0"} />
            <input type="hidden" name="utm_campaign" value={utm_campaign || ""} />
            <input type="hidden" name="utm_term" value={utm_term || ""} />
            <input type="hidden" id="subject" name="subject" value={`Demande de devis de ${email}`}></input>
            <p className="hidden">
              <label>Ne remplissez pas ce champ si vous êtes un humain: <input name="potdemiel" /></label>
            </p>

            <p className="text-sm font-bold uppercase tracking-widest text-orange mb-2">
              Demande rapide et gratuite
            </p>
            <p id="cart-modal-title" className="text-2xl md:text-3xl font-bold text-blue mb-2 pr-8">
              Recevez votre devis personnalisé
            </p>
            <p className="text-gray-600 mb-6">
              Notre équipe vous répond en 24 H
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 mb-5">
              <div>
                <label htmlFor="devis-societe" className={labelClass}>Société</label>
                <input id="devis-societe" name="societe" type="text" autoComplete="organization" placeholder="Nom de votre entreprise" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="devis-nom" className={labelClass}>Nom et prénom</label>
                <input id="devis-nom" name="Nom" type="text" autoComplete="name" placeholder="Votre nom" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="devis-email" className={labelClass}>E-mail professionnel <span className="text-red-danger">*</span></label>
                <input id="devis-email" required name="email" type="email" autoComplete="email" placeholder="nom@entreprise.fr" onChange={(e) => setEmail(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="devis-tel" className={labelClass}>Téléphone</label>
                <input id="devis-tel" name="tel" type="tel" autoComplete="tel" placeholder="06 00 00 00 00" className={fieldClass} />
              </div>
            </div>

            <p className={labelClass}>Votre logo ou visuel</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <FileDrop name="file" id="logo-import" title="Ajouter un fichier" hint="Face A · PDF, AI, EPS, SVG ou PNG · Facultatif" />
              <FileDrop name="fileB" id="logo-importB" title="Ajouter un 2ᵉ fichier" hint="Face B ou 2ᵉ logo · Facultatif" />
            </div>

            <label htmlFor="devis-message" className={labelClass}>Précisions sur votre projet</label>
            <textarea
              id="devis-message"
              name="message"
              rows={2}
              placeholder="Un délai, une couleur Pantone, une contrainte particulière…"
              className="block w-full mb-6 px-4 py-3 text-base bg-white border-1 border-gray-300 rounded-lg focus:outline-none focus:border-blue"
            />

            {[0, 1, 2, 3, 4, 5].map((i) => (
              <input
                key={i}
                type="hidden"
                name={`produit_${i + 1}`}
                value={cart[i] ? `${cart[i].product.title} | ${cart[i].quantity} pcs | ${cart[i].color ? cart[i].color.Nom : ''} | ${cart[i].marquage} | ${cart[i].faces} face(s)` : 'pas de produit ajouté'} />
            ))}

            <button
              type="submit"
              disabled={isSubmitting}
              className="block w-full py-4 rounded-lg bg-orange hover:bg-red-orange text-white font-bold uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Envoi en cours..." : "Recevoir mon devis sous 24 h"}
            </button>

            <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 py-3 mt-6 border-t-1 border-b-1 border-gray-200 text-sm text-gray-600">
              <AvisVerifies height="26" width="72" aria-label="Avis Vérifiés" role="img" />
              <span>
                <span className="text-orange tracking-tighter">★★★★★</span>{" "}
                <strong className="text-gray-800">4,9/5</strong>
              </span>
            </div>

            <ul className="flex flex-wrap justify-between gap-x-4 gap-y-3 mt-5">
              {engagements.map(({ Icon, text }, index) => (
                <li key={index} className="flex items-center gap-2 text-xs font-bold uppercase text-gray-800">
                  <Icon height="40" width="40" className="flex-shrink-0" />
                  {/* 2 lignes max : chaque ligne (séparée par <br />) ne se coupe pas, l'item passe à la ligne suivante si besoin */}
                  <span className="whitespace-nowrap">{text}</span>
                </li>
              ))}
            </ul>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CartModal;
